import React, { useEffect, useState } from "react";
import Navbar from "./navbar";
import Footer from "./footer";
import { toast } from "react-toastify";
import {
  confirmSubscription,
  getPlans,
  takeSubscription,
} from "../../services/services";
import { useNavigate } from "react-router-dom";
import { FaCheck, FaInfoCircle, FaTimes } from "react-icons/fa";
import { GST } from "../../constants";

const highlightColor = "#0b2b6d";

const Pricing = () => {
  const navigate = useNavigate();
  const [billing, setBilling] = useState("monthly");
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showTooltip, setShowTooltip] = useState(null);

  const user = JSON.parse(localStorage.getItem("Login_User") || "{}");

  // Fetch Plans from API
  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await getPlans();
      if (res.data?.status) {
        setPlans(res.data.response || []);
      } else {
        toast.error(res.data?.message || "Failed to fetch plans");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleSelectPlan = (plan) => {
    if (!user?.phone) {
      navigate("/login");
      return;
    }

    const price =
      billing === "monthly"
        ? plan.pricing?.monthly?.INR
        : plan.pricing?.yearly?.INR;

    setSelectedPlan({
      ...plan,
      billingCycle: billing,
      price: price || 0,
      label: plan.name,
      planId: plan._id,
    });
  };

  const closeSidebar = () => setSelectedPlan(null);

  const numericPrice = selectedPlan?.price || 0;
  const gstAmount = numericPrice * Number(GST || 0.18);
  const totalAmount = numericPrice + gstAmount;

  const handleProceedPayment = async () => {
    if (!selectedPlan) return;

    const payload = {
      phone: user?.phone,
      planId: selectedPlan.planId,
      billing_cycle: selectedPlan.billingCycle,
    };

    try {
      const res = await takeSubscription(payload);
      if (res.data?.status) {
        openRazorpay(res.data.response);
      } else {
        toast.error(res.data?.message || "Something went wrong");
      }
    } catch (error) {
      toast.error("Payment initiation failed");
      console.error(error);
    }
  };

  const openRazorpay = (data) => {
    const { order_details } = data;

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: order_details.amount,
      currency: order_details.currency,
      name: "Assist Buddi Books",
      description: `${selectedPlan?.name} - ${selectedPlan?.billingCycle}`,
      order_id: order_details.id,
      notes: {
        invoice_ref: `AB_SUB_${data.subs_id}`,
      },
      image: "https://book.assistbuddi.com/ab_book_logo_icon.png",
      prefill: {
        name: user?.name,
        email: user?.email,
        contact: user?.phone,
      },
      handler: async function (response) {
        try {
          const res = await confirmSubscription({
            order_id: response.razorpay_order_id,
          });

          if (res.data.status) {
            const user = res.data.response;
            toast.success("Payment confirmed successfully");
            setSelectedPlan(null);
            localStorage.setItem("Login_User", JSON.stringify(user));
            window.location.href = "/dashboard";
          } else {
            toast.error(res.data.message || "Confirmation failed");
          }
        } catch (error) {
          toast.error("Payment verification failed");
          console.error(error);
        }
      },
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();

    razorpay.on("payment.failed", (err) => {
      toast.error(err.error.description || "Payment failed");
    });
  };

  return (
    <>
      <Navbar />

      <div className="container py-5 mt-4">
        <div className="text-center mb-5 mt-5">
          <span className="partner-gradient-text fw-bold fs-2 ">
            Plans & Billing
          </span>
          <p className="text-muted">
            Choose the plan that best fits your business needs
          </p>

          {/* Billing Toggle */}
          <div className="d-inline-flex border rounded-pill p-1 bg-white mt-3">
            <button
              className={`btn btn-sm rounded-pill px-4 ${billing === "monthly" ? "btn-primary text-white" : "btn-light"}`}
              onClick={() => setBilling("monthly")}
            >
              Monthly
            </button>
            <button
              className={`btn btn-sm rounded-pill px-4 ${billing === "yearly" ? "btn-primary text-white" : "btn-light"}`}
              onClick={() => setBilling("yearly")}
            >
              Yearly <span className="text-success">{/* (17% off) */}</span>
            </button>
          </div>
          <p className="text-muted mt-2">Start your free 14-day trial</p>
        </div>

        {/* Plans Cards */}
        {loading ? <div
          className="d-flex align-items-center justify-content-center p-5"
          style={{ height: "100vh" }}
        >
          <div className="spinner-border text-secondary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div> :
          <div className="row g-4 justify-content-center">
            {plans.map((plan, index) => {
              const price =
                billing === "monthly"
                  ? plan.pricing?.monthly?.INR
                  : plan.pricing?.yearly?.INR;

              const isPopular = plan.name.toLowerCase() === "pro";

              return (
                <div key={plan._id} className="col-lg-4 col-md-6 ">
                  <div
                    className={`card h-100 shadow-sm position-relative overflow-visible ${isPopular ? "border-primary" : ""
                      }`}
                    style={{
                      borderRadius: "16px",
                      border: isPopular ? "2px solid #0b2b6d" : "1px solid #ddd",
                    }}
                  >
                    {isPopular && (
                      <div className="position-absolute top-0 start-50 translate-middle-x bg-primary text-white px-4  rounded-bottom fw-bold ">
                        Most Popular
                      </div>
                    )}

                    <div className="card-body p-4  text-center ">
                      <h4 className="fw-bold mb-3 mt-3 partner-gradient-text ">
                        {plan.name}
                      </h4>
                      <hr className="my-2 " />
                      <div className="my-4">
                        <span className="fs-1 fw-bold">₹{price || 0}</span>
                        <span className="text-muted fs-5">
                          /{billing === "monthly" ? "month" : "year"}
                        </span>
                      </div>

                      <button
                        className={`btn w-100 py-3 rounded-pill fw-semibold mb-4 ${isPopular ? "btn-primary" : "btn-outline-primary"
                          }`}
                        onClick={() => handleSelectPlan(plan)}
                      >
                        {plan.name === "Basic"
                          ? "Get Started"
                          : `Get ${plan.name}`}
                      </button>

                      <div className="text-start">
                        <ul className="list-unstyled">
                          {plan.features?.map((feat, i) => (
                            <li
                              key={i}
                              className="d-flex align-items-center gap-2 py-1 position-relative"
                            >
                              <FaCheck className="text-success flex-shrink-0" />

                              <span className="text-start">{feat.name}</span>

                              {/* Info Icon + Tooltip */}
                              {feat.description && (
                                <div
                                  className="position-relative"
                                  onMouseEnter={() =>
                                    setShowTooltip(`${plan._id}-${i}`)
                                  }
                                  onMouseLeave={() => setShowTooltip(null)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowTooltip(
                                      showTooltip === `${plan._id}-${i}`
                                        ? null
                                        : `${plan._id}-${i}`,
                                    );
                                  }}
                                >
                                  <FaInfoCircle
                                    className="text-muted"
                                    style={{
                                      cursor: "pointer",
                                      fontSize: ".89rem",
                                    }}
                                  />

                                  {/* Tooltip */}
                                  {showTooltip === `${plan._id}-${i}` && (
                                    <div
                                      className="tooltip-box"
                                      style={{
                                        position: "absolute",
                                        bottom: "100%",
                                        right: "0",
                                        backgroundColor: "#333",
                                        color: "#fff",
                                        padding: "10px 14px",
                                        borderRadius: "8px",
                                        width: "220px",
                                        fontSize: "0.85rem",
                                        zIndex: 9999,
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                                        marginBottom: "8px",
                                      }}
                                    >
                                      {feat.description}
                                    </div>
                                  )}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="mt-5 d-flex justify-content-center">
              <div
                className="d-flex align-items-center justify-content-center gap-3 px-4 py-3 rounded-pill"
                style={{
                  backgroundColor: "#e3f2fd",
                  borderRadius: "50px",
                  maxWidth: "fit-content",
                }}
              >
                <h6 className="text-muted mb-0">
                  Need a custom solution for your business?
                </h6>

                <button
                  className="btn btn-primary rounded-pill px-4 p-3"
                  onClick={() => {
                    const message = encodeURIComponent(
                      "Hello Assist Buddi Books \n\nI'm interested in the Enterprise plan and would like to know more details.",
                    );
                    window.open(
                      `https://wa.me/8084378326?text=${message}`,
                      "_blank",
                    );
                  }}
                >
                  Contact Sales
                </button>
              </div>
            </div>
          </div>
        }
      </div>

      {/* SIDEBAR  */}
      {selectedPlan && (
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.55)",
              zIndex: 1040,
            }}
            onClick={closeSidebar}
          />

          <div
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "380px",
              maxWidth: "90vw",
              height: "100%",
              backgroundColor: "#fff",
              boxShadow: "-10px 0 30px rgba(0,0,0,0.25)",
              zIndex: 1050,
              overflowY: "auto",
            }}
          >
            <div className="p-4 d-flex flex-column h-100">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0 fw-bold">Plan Summary</h4>
                <button
                  className="btn btn-link text-dark p-0"
                  onClick={closeSidebar}
                >
                  <FaTimes size={22} />
                </button>
              </div>

              {/* Account Details */}
              <div className="mb-4 p-3 bg-light rounded">
                <h6 className="fw-bold mb-2">Account Details</h6>
                <div className="small text-muted">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Name</span>
                    <span className="fw-semibold text-dark">
                      {user.name || ""}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between mb-1">
                    <span>Email</span>
                    <span className="fw-semibold text-dark">
                      {user.email || ""}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Phone</span>
                    <span className="fw-semibold text-dark">
                      {user.phone || ""}
                    </span>
                  </div>
                </div>
              </div>

              {/* Plan Summary */}
              <div className="mb-4 p-3 border rounded bg-light">
                <div className="fw-bold mb-3 fs-5">
                  {selectedPlan.name} Plan
                </div>

                <div className="d-flex justify-content-between mb-2 small">
                  <span className="text-muted">Plan Amount</span>
                  <span>₹{numericPrice.toFixed(2)}</span>
                </div>

                <div className="d-flex justify-content-between mb-2 small">
                  <span className="text-muted">GST (18%)</span>
                  <span>₹{gstAmount.toFixed(2)}</span>
                </div>

                <hr className="my-2" />

                <div className="d-flex justify-content-between fw-bold fs-5">
                  <span>Total Payable</span>
                  <span className="text-primary">
                    ₹{totalAmount.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="row mt-auto g-2">
                <div className="col-12 col-md-5">
                  <button
                    className="btn btn-secondary py-2 w-100"
                    onClick={closeSidebar}
                  >
                    Cancel
                  </button>
                </div>
                <div className="col-12 col-md-7">
                  <button
                    className="btn btn-primary w-100 text-nowrap py-2"
                    onClick={handleProceedPayment}
                    style={{
                      backgroundColor: highlightColor,
                      borderColor: highlightColor,
                    }}
                  >
                    Proceed to Payment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <Footer />
    </>
  );
};

export default Pricing;
